import { z } from 'zod';
import i18next from 'i18next';
import { http, API_BASE, getAuthToken, localeHeaders, ApiError } from '@/services/http';
import {
  buildHexisChatBody, buildHexisApproveBody, createHexisChatStream,
  type HexisReply, type HexisApproveResult,
} from '@marcianus/shared/hexis-client';

const pendingActionSchema = z.object({
  token: z.string(),
  tool: z.string(),
  summary: z.string().nullish().transform((s) => s ?? ''),
}).nullish();
const chatSchema = z.object({
  reply: z.string().nullish(),
  suggestions: z.array(z.string()).nullish(),
  intent: z.string().nullish(),
  pending_action: pendingActionSchema,
}).passthrough();
export type { HexisReply };

/** Copiloto Hexis → POST /hexis/chat (Legacy non-stream) */
export async function askHexis(params: {
  message: string;
  userId: string;
  eventId?: string;
  context?: { tab: string; subtab: string; label: string };
  locale?: string;
}): Promise<HexisReply> {
  const raw = await http.post<unknown>('/hexis/chat', buildHexisChatBody({
    message: params.message,
    userId: params.userId,
    eventId: params.eventId,
    context: params.context,
    locale: params.locale,
  }));
  return chatSchema.parse(raw ?? {});
}

// Chunk normalizado que a HexisPage consome (Chain of Thought + resposta).
export type HexisStreamChunk =
  | { type: 'think' | 'tool'; content: string; name?: string }
  | { type: 'reply'; content: string };

/**
 * Copiloto Hexis em streaming → POST /hexis/ai/chat (SSE).
 *
 * Este é o endpoint REAL do cérebro (registado em /api/v1/hexis/ai/chat, guardado
 * por optionalAuth). O servidor emite eventos `{type:'token'|'step'|'done'|'error'}`;
 * aqui traduzimo-los para o contrato think/tool/reply que a UI já entende — a
 * tradução mora num sítio só, e a página não precisa de saber o formato do fio.
 *
 * Correções face à versão anterior (que apontava para o vazio):
 *   • URL: API_BASE (/api/v1) + /hexis/ai/chat — antes ia para /hexis/chat/stream
 *     (inexistente) na origem estática da Hostinger.
 *   • Auth: token de sessão Clerk (getAuthToken) — antes lia um marc:access_token
 *     do localStorage que o cutover extinguiu.
 *   • Contrato: mapeia token/step/done — antes esperava reply/think/tool que o
 *     servidor nunca envia.
 */
export async function* askHexisStream(params: {
  message: string;
  userId: string;
  eventId?: string;
  context?: { tab: string; subtab: string; label: string };
  locale?: string;
}): AsyncGenerator<HexisStreamChunk, void, unknown> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/hexis/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...localeHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      message: params.message,
      currentPage: params.context?.label || '/hexis',
      ...(params.locale ? { locale: params.locale } : {}),
    }),
  });

  if (!response.ok || !response.body) {
    // Mantém o estado HTTP: «sobrecarregado» (503/504) e «sem sessão» (401) pedem mensagens diferentes.
    throw new ApiError(response.status, 'Falha ao iniciar stream do Hexis');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let gotTokens = false;   // houve streaming token-a-token?

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || ''; // o último frame pode estar incompleto

    for (const frame of frames) {
      // Linhas de comentário SSE (': hb' keepalive) são ignoradas.
      const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!dataLine) continue;
      const dataStr = dataLine.slice(6).trim();
      if (!dataStr || dataStr === '[DONE]') continue;

      let ev: { type?: string; token?: string; label?: string; response?: string; message?: string };
      try { ev = JSON.parse(dataStr); } catch { continue; }

      if (ev.type === 'token' && ev.token) {
        gotTokens = true;
        yield { type: 'reply', content: ev.token };
      } else if (ev.type === 'step' && ev.label) {
        yield { type: 'think', content: ev.label };
      } else if (ev.type === 'done') {
        // Com streaming, os tokens já compuseram a resposta. Mas as respostas das GUARDAS
        // (injeção, privacidade, fora de âmbito) e as de cache/dados chegam SÓ no `done`
        // (campo `response`), sem tokens: antes eram descartadas e a UI ficava calada e bloqueada.
        if (!gotTokens && ev.response) yield { type: 'reply', content: ev.response };
        return;
      } else if (ev.type === 'error') {
        throw new Error(ev.message || 'Erro no stream do Hexis');
      }
    }
  }
}

/** Aprova uma ação proposta pelo Hexis (Human-in-the-Loop). */
export async function approveHexisAction(params: { token: string; userId: string }): Promise<HexisApproveResult> {
  return http.post<HexisApproveResult>('/hexis/tool/approve',
    buildHexisApproveBody(params.token, params.userId));
}

/**
 * O MESMO `/hexis/chat`, com progresso: os passos reais do cérebro chegam
 * enquanto pensa e o payload final é idêntico ao de `askHexis` — por isso o
 * function calling (pending_action) e as sugestões continuam todos cá.
 *
 * O `open` injeta a base e o token da app; o parser vive no shared, partilhado
 * com a consola HQ.
 */
export const askHexisComProgresso = createHexisChatStream(async (path, body) => {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...localeHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  return { ok: res.ok, body: res.body };
});

/**
 * O que dizer quando o Hexis falha — cada causa tem a sua frase (antes: «offline» para TUDO, incluindo
 * o 504 de um LLM sobrecarregado, o que fazia parecer que a plataforma estava em baixo).
 */
export function hexisErrorText(err: unknown): string {
  const tr = (k: string, pt: string) => i18next.isInitialized ? i18next.t(`err.${k}`, { ns: 'hexis', defaultValue: pt }) : pt;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return tr('offline', 'Sem ligação à internet. Verifica a rede e tenta novamente.');
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return tr('auth', 'Entra na tua conta para falar com o Hexis.');
    if (err.status === 429) return tr('rate', 'Estás a perguntar depressa demais. Espera um instante e tenta novamente.');
    if (err.status === 408 || err.status === 502 || err.status === 503 || err.status === 504) {
      return tr('overload', 'O Hexis está sobrecarregado neste momento e não conseguiu responder a tempo. Tenta novamente daqui a pouco.');
    }
  }
  return tr('generic', 'O Hexis não conseguiu responder. Tenta novamente.');
}
